import { FC } from 'react'

import styled, { css } from 'styled-components'

import PaperclipIcon from 'src/Assets/Svg/paperclip.svg?react'
import CloseIcon from 'src/Assets/Svg/close-cross.svg?react'
import { variables } from 'src/Styled/variables'

import { formatFileSize } from './validation'

export type AudioFileUploadTrackItemProps = {
  name: string
  size?: number
  progress?: number
  uploaded?: boolean
  onRemove?: () => void
}

const MIN_PROGRESS = 0
const MAX_PROGRESS = 100

const normalizeProgress = (progress: number): number =>
  Number.isFinite(progress)
    ? Math.round(Math.min(MAX_PROGRESS, Math.max(MIN_PROGRESS, progress)))
    : MIN_PROGRESS

const Container = styled.div<{ $uploaded: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 8px 8px 8px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};

  ${({ $uploaded }) =>
    $uploaded &&
    css`
      border-color: ${({ theme }) => theme.color.brandGreen};
    `}
`

const IconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.darkGrey};
`

const Name = styled.div`
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Details = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
  text-align: right;
  white-space: nowrap;
`

const RemoveButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 40px;
  background: transparent;
  color: ${({ theme }) => theme.color.darkGrey};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandGreen};
    outline-offset: 2px;
  }
`

export const AudioFileUploadTrackItem: FC<AudioFileUploadTrackItemProps> = ({
  name,
  size,
  progress,
  uploaded,
  onRemove,
}) => (
  <Container
    $uploaded={Boolean(uploaded)}
    aria-label={`${name}, ${uploaded ? 'uploaded' : 'uploading'}`}
  >
    <IconWrapper>
      <PaperclipIcon width={16} height={16} />
    </IconWrapper>
    <Name title={name}>{name}</Name>
    <Details aria-live='polite'>
      {!uploaded && typeof progress === 'number' && (
        <span>{normalizeProgress(progress)}%</span>
      )}
      {typeof size === 'number' && <span>{formatFileSize(size)}</span>}
    </Details>
    <RemoveButton
      type='button'
      aria-label={`Remove ${name}`}
      onClick={onRemove}
    >
      <CloseIcon width={16} height={16} />
    </RemoveButton>
  </Container>
)
